import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { ITransaction } from 'src/_models/order.model'
import { useUserStore } from 'src/_store/user.store'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { ColumnDef } from '@tanstack/react-table'
import { formatAuctionDate } from 'src/lib/format-auction'
import currencyFormatter from 'src/components/ui/currency-formatter'
import { download, generateCsv, mkConfig } from 'export-to-csv'
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover'
import { Button } from 'src/components/ui/button'
import { DataTable } from 'src/components/data-table'
import { Dialog, DialogContent } from 'src/components/ui/dialog'
import AuctionFilterBtn from 'src/components/auction/auction-filter-btn'
import * as React from 'react'
import OrderDialog from 'src/components/order/order-dialog'

const TransactionHistoryTable = ({ heading, transactions }: { heading: string; transactions: ITransaction[] }) => {
  const { t } = useTranslation('translation')
  const [selectedOrder, setSelectedOrder] = useState<ITransaction | null>(null)

  const { user } = useUserStore((state) => {
    return {
      user: state.user,
    }
  })

  const [showOrderStatusModal, setShowOrderStatusModal] = useState<boolean>(false)

  const columnsOrders: ColumnDef<ITransaction>[] = [
    {
      accessorKey: 'createdAt',
      header: 'DATE',
      cell: ({ row }) => {
        return <div className="text-center">{formatAuctionDate(row.original.createdAt)}</div>
      },
    },
    {
      accessorKey: 'order_id',
      header: 'Transaction ID',
      cell: ({ row }) => {
        return <div className="text-center">{row.original.id}</div>
      },
    },
    {
      accessorKey: 'auction_type',
      header: 'Auction Type',
      cell: ({ row }) => {
        return <div className="text-center">{t(`auctionType.${row.original.order.auction.auctionType}`)}</div>
      },
    },
    {
      accessorKey: 'fuel_type',
      header: 'FUEL TYPE',
      cell: ({ row }) => {
        return (
          <div className="text-center">
            {row.original.order.auction.fuelColor.name} {row.original.order.auction.fuelType.name}{' '}
            {row.original.order.auction.fuelPurityLevel.purity} (
            {t('fuelStateShorthand.' + row.original.order.auction.fuelState)})
          </div>
        )
      },
    },
    {
      accessorKey: 'quantity',
      header: 'QUANTITY',
      cell: ({ row }) => {
        return (
          <div className="text-center">
            {row.original.order.auction.fuelWeight} {t('fuelWeightUnit.' + row.original.order.auction.fuelWeightUnit)}
          </div>
        )
      },
    },
    {
      accessorKey: 'carbonIntensity',
      header: 'Carbon Intensity',
      cell: ({ row }) => {
        return <div className="text-center">{row.original.order.auction.carbonIntensity || '-'}</div>
      },
    },
    {
      accessorKey: 'price_per_kg',
      header: 'Price/Kg',
      cell: ({ row }) => {
        return (
          <div className="text-center">
            {currencyFormatter({
              currency: row.original.order.auction.sellingCurrency,
              amount: row.original.order.auction.sellingPricePerUnit,
              cents: true,
            })}
          </div>
        )
      },
    },
    {
      accessorKey: 'buyer_name',
      header: 'Buyer Name',
      cell: ({ row }) => {
        return <div className="text-center">{row.original.order.buyer.companyName}</div>
      },
    },
    {
      accessorKey: 'userTransactionType',
      header: 'Transaction Type',
      cell: ({ row }) => {
        return (
          <div className="text-center">
            PURCHASE
            {/*{row.original.userTransactionType}*/}
          </div>
        )
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => {
        return (
          <div className="text-right">
            {currencyFormatter({
              currency: row.original.currency,
              amount: row.original.amount,
              cents: true,
            })}
          </div>
        )
      },
    },
    {
      accessorKey: 'action',
      header: '',
      cell: ({ row }) => {
        // const payment = row.original

        return (
          <a
            onClick={() => {
              setSelectedOrder(row.original)
              setShowOrderStatusModal(true)
            }}
            className="cursor-pointer font-bold text-[#0077B6] underline"
          >
            View&nbsp;Order
          </a>
        )
      },
    },
  ]

  const exportCSV = () => {
    const csvConfig = mkConfig({
      fieldSeparator: ',',
      filename: heading, // export file name (without .csv)
      decimalSeparator: '.',
      useKeysAsHeaders: true,
    })
    const rowData = transactions.map((row) => ({
      DATE: formatAuctionDate(row.createdAt),
      'ORDER ID': row.id,
      'Auction Type': t(`auctionType.${row.order.auction.auctionType}`),
      'FUEL TYPE': row.order.auction.fuelType.name,
      COLOR: row.order.auction.fuelColor.name,
      'PURITY LEVEL': row.order.auction.fuelPurityLevel.purity,
      QUANTITY: `${row.order.auction.fuelWeight} ${t('fuelWeightUnit.' + row.order.auction.fuelWeightUnit)}`,
      'Price/Kg':
        t('currency.' + row.order.auction.sellingCurrency) + ` ${row.order.auction.sellingPricePerUnit / 100}`,
      'Buyer Name': row.order.buyer.companyName,
      'Transaction Type': 'PURCHASE',
      Amount: t('currency.' + row.currency) + ` ${row.amount / 100}`,
    }))
    const csv = generateCsv(csvConfig)(rowData)
    download(csvConfig)(csv)
  }

  return (
    <div className="container mx-auto bg-transparent ">
      <div className="mb-[15px] flex w-full items-center justify-between pt-10 align-middle">
        <h2 className="mb-2 text-lg font-bold text-secoundary 2xl:text-xl">{heading}</h2>
        <span className="relative text-sm">
          <div className="flex gap-[10px]">
            <AuctionFilterBtn />
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" size="icon" variant="iconTable" className="py-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="24" viewBox="0 0 10 13" fill="none">
                    <path
                      d="M0.5 12.5645H10V11.1527H0.5M10 4.79975H7.28572V0.564453H3.21429V4.79975H0.5L5.25 9.74092L10 4.79975Z"
                      fill="#333333"
                    ></path>
                  </svg>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48" align="end">
                <div className="cursor-pointer" onClick={exportCSV}>
                  Export As CSV
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {/*{showFilter && (*/}
          {/*  <FilterComponent*/}
          {/*    // className="absolute right-0"*/}
          {/*    onCancel={() => setShowFilter(false)}*/}
          {/*  />*/}
          {/*)}*/}
        </span>
      </div>

      <DataTable variant="column-bg-grey" columns={columnsOrders} data={transactions} headerHidden={false} />

      {selectedOrder && (
        <Dialog open={showOrderStatusModal} onOpenChange={(value) => setShowOrderStatusModal(value)}>
          <OrderDialog
            orderId={selectedOrder.order.id}
            setShowOrderStatusModal={(showDialog) => setShowOrderStatusModal(showDialog)}
          />
        </Dialog>
      )}
    </div>
  )
}

export default TransactionHistoryTable
